import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { authApi, formatApiError } from "../lib/auth";
import { KeyRound, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

const MIN_PASSWORD_LENGTH = 6;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // "success" | "expired" | null
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (result === "success") {
      const timer = setTimeout(() => navigate("/login"), 2000);
      return () => clearTimeout(timer);
    }
  }, [result, navigate]);

  // ---- No token in URL ----
  if (!token) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-slate-50 to-slate-200"
        data-testid="reset-page"
      >
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <AlertTriangle className="h-5 w-5 text-rose-500" /> Invalid reset link
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <p className="text-slate-600">
              This reset link is missing its token. Please request a new one.
            </p>
            <Link
              to="/forgot-password"
              className="text-slate-900 underline hover:text-slate-700"
            >
              Request a new reset link
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setBusy(true);
    try {
      await authApi.resetPassword(token, newPassword);
      setResult("success");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 400) {
        setResult("expired");
      } else {
        setError(
          formatApiError(err) || "Something went wrong. Please try again."
        );
      }
    } finally {
      setBusy(false);
    }
  };

  // ---- Success state ----
  if (result === "success") {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-slate-50 to-slate-200"
        data-testid="reset-page"
      >
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" /> Password updated
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600">
              Password updated — redirecting to sign in…
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- Expired / already-used token state ----
  if (result === "expired") {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-slate-50 to-slate-200"
        data-testid="reset-page"
      >
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <AlertTriangle className="h-5 w-5 text-rose-500" /> Link expired
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <p className="text-slate-600">
              This reset link has expired or has already been used. Request a new one.
            </p>
            <Link
              to="/forgot-password"
              className="text-slate-900 underline hover:text-slate-700"
            >
              Request a new reset link
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- Main form ----
  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-slate-50 to-slate-200"
      data-testid="reset-page"
    >
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <KeyRound className="h-5 w-5" /> Set new password
          </CardTitle>
          <p className="text-sm text-slate-500">ZAMBA Product Selector</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="reset-new-password">New password</Label>
              <Input
                id="reset-new-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD_LENGTH}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="reset-new-password"
              />
              <div className="text-xs text-slate-500 mt-1">
                Min {MIN_PASSWORD_LENGTH} characters.
              </div>
            </div>
            <div>
              <Label htmlFor="reset-confirm-password">Confirm password</Label>
              <Input
                id="reset-confirm-password"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                data-testid="reset-confirm-password"
              />
            </div>
            {error && (
              <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={busy}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="reset-submit-btn"
            >
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Updating…
                </>
              ) : (
                "Set new password"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
